import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, CheckCircle, Loader2, Search, Upload } from 'lucide-react';
import axios from 'axios';
import CarteGriseScanner from '../components/CarteGriseScanner';
import FranchiseInfoNote from '../components/FranchiseInfoNote';
import { VEHICLE_BRANDS, ELIGIBLE_YEARS } from '../data/vehicleBrands';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriceCalculatorV4 = () => {
  const { subscriptionType } = useParams();
  const navigate = useNavigate();

  // Lookup state
  const [lookupType, setLookupType] = useState('homologation');
  const [homologationInput, setHomologationInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  // Manual entry state - can be used to complete partial data
  const [manualBrand, setManualBrand] = useState('');
  const [manualModel, setManualModel] = useState('');
  const [manualYear, setManualYear] = useState('');
  const [manualDisplacement, setManualDisplacement] = useState('');
  const [manualPower, setManualPower] = useState('');
  const [isCompletingData, setIsCompletingData] = useState(false);
  const [missingFieldsList, setMissingFieldsList] = useState([]);

  // Carte Grise OCR data
  const [carteGriseData, setCarteGriseData] = useState(null);
  const [showCarteGriseScanner, setShowCarteGriseScanner] = useState(false);

  // Vehicle data
  const [vehicleData, setVehicleData] = useState(null);
  const [dataSource, setDataSource] = useState(''); // 'Homologation', 'Manual', 'CarteGrise'

  // Pricing state
  const [franchise, setFranchise] = useState(200);
  const [abonnement, setAbonnement] = useState(subscriptionType?.toUpperCase() || 'S');
  const [priceResult, setPriceResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Config
  const [config, setConfig] = useState(null);

  const subscriptionNames = {
    S: 'Abonnement S',
    M: 'Abonnement M',
    L: 'Abonnement L'
  };

  // Validate subscription type (run only once on mount)
  useEffect(() => {
    if (!['S', 'M', 'L'].includes(subscriptionType?.toUpperCase())) {
      navigate('/abonnements', { replace: true });
    } else {
      setAbonnement(subscriptionType.toUpperCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Only run on mount, not when navigate changes

  // Load config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/v4/config`);
        setConfig(response.data);
      } catch (error) {
        console.error('Error loading config:', error);
      }
    };
    fetchConfig();
  }, []);

  // Auto-calculate when vehicle data and selections are complete
  useEffect(() => {
    if (vehicleData && vehicleData.complete && abonnement && franchise !== null) {
      handleCalculatePrice();
    }
  }, [vehicleData, abonnement, franchise]);

  // Handle Homologation lookup
  const handleHomologationLookup = async () => {
    // Remove only spaces, keep all other characters
    const cleanHomologation = homologationInput.replace(/ /g, '');
    
    if (!cleanHomologation) {
      setLookupError('Veuillez entrer un numéro d\'homologation');
      return;
    }

    setLookupLoading(true);
    setLookupError('');
    setPriceResult(null);

    try {
      const response = await axios.post(`${API}/v4/vehicle/lookup`, {
        lookup_type: 'homologation',
        lookup_value: cleanHomologation
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Count missing required fields
        const missingFields = [];
        if (!data.brand) missingFields.push('marque');
        if (!data.year) missingFields.push('année');
        if (!data.displacement_cc) missingFields.push('cylindrée');
        if (!data.power_ch) missingFields.push('puissance');
        
        // Check if data is complete or nearly complete
        if (data.complete || missingFields.length === 0) {
          // All data available
          setVehicleData(data);
          setDataSource('Homologation');
          setLookupError('');
          setIsCompletingData(false);
          setMissingFieldsList([]);
        } else if (missingFields.length === 1) {
          // Only 1 field missing - allow completion below
          setLookupError(
            `✅ Véhicule trouvé ! Information manquante : ${missingFields[0]}. ` +
            `Veuillez la compléter ci-dessous.`
          );
          setIsCompletingData(true);
          setMissingFieldsList(missingFields);
          setManualBrand(data.brand || '');
          setManualModel(data.model || '');
          setManualYear(data.year ? String(data.year) : '');
          setManualDisplacement(data.displacement_cc ? String(data.displacement_cc) : '');
          setManualPower(data.power_ch ? String(data.power_ch) : '');
        } else {
          // 2+ fields missing - suggest manual entry
          setLookupError(
            `❌ Recherche impossible avec ce numéro d'homologation (${missingFields.length} informations manquantes : ${missingFields.join(', ')}). ` +
            `Veuillez utiliser l'onglet "Saisie manuelle".`
          );
          setVehicleData(null);
          setIsCompletingData(false);
        }
      } else {
        setLookupError(response.data.message || 'Numéro d\'homologation non trouvé. Veuillez utiliser la saisie manuelle.');
        setVehicleData(null);
      }
    } catch (error) {
      console.error('Homologation lookup error:', error);
      setLookupError('Erreur lors de la recherche. Veuillez réessayer ou entrer les données manuellement.');
      setVehicleData(null);
    } finally {
      setLookupLoading(false);
    }
  };

  // Handle manual data submission
  const handleManualSubmit = async () => {
    // Validate inputs
    if (!manualBrand || !manualYear || !manualDisplacement || !manualPower) {
      setLookupError('Veuillez remplir tous les champs requis');
      return;
    }

    setLookupLoading(true);
    setLookupError('');
    setPriceResult(null);

    try {
      const response = await axios.post(`${API}/v4/vehicle/manual`, {
        brand: manualBrand,
        model: manualModel || null,
        year: parseInt(manualYear),
        displacement_cc: parseInt(manualDisplacement),
        power_ch: parseInt(manualPower)
      });

      if (response.data.success && response.data.data) {
        setVehicleData(response.data.data);
        setDataSource('Manual');
        setLookupError('');
      } else {
        setLookupError('Données invalides. Veuillez vérifier vos entrées.');
        setVehicleData(null);
      }
    } catch (error) {
      console.error('Manual submit error:', error);
      setLookupError('Erreur lors du traitement. Veuillez vérifier vos données.');
      setVehicleData(null);
    } finally {
      setLookupLoading(false);
    }
  };

  // Calculate price
  const handleCalculatePrice = async () => {
    if (!vehicleData) {
      return;
    }

    setCalculating(true);

    try {
      const response = await axios.post(`${API}/v4/price/calculate`, {
        vehicle_data: vehicleData,
        abonnement: abonnement,
        franchise_chf: franchise
      });

      setPriceResult(response.data);
    } catch (error) {
      console.error('Price calculation error:', error);
      setPriceResult({
        success: false,
        eligible: false,
        reasons: ['Erreur lors du calcul. Veuillez réessayer.']
      });
    } finally {
      setCalculating(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setHomologationInput('');
    setManualBrand('');
    setManualModel('');
    setManualYear('');
    setManualDisplacement('');
    setManualPower('');
    setVehicleData(null);
    setDataSource('');
    setPriceResult(null);
    setLookupError('');
    setIsCompletingData(false);
    setMissingFieldsList([]);
    setCarteGriseData(null);
  };

  // Handle Carte Grise data from OCR (new CarteGriseScanner returns {data, legacy})
  const handleCarteGriseData = ({ data: _strictData, legacy }) => {
    const data = legacy || {};
    setCarteGriseData(data);
    
    // Pre-fill manual entry fields
    if (data.brand) setManualBrand(data.brand);
    if (data.model) setManualModel(data.model);
    if (data.year) setManualYear(String(data.year));
    if (data.displacement_cc) setManualDisplacement(String(data.displacement_cc));
    if (data.power_ch) setManualPower(String(data.power_ch));
    if (data.homologation) setHomologationInput(data.homologation);
    
    // Close scanner modal
    setShowCarteGriseScanner(false);
    
    // Switch to manual entry tab to show pre-filled data
    setLookupType('manual');
    
    // If we have complete data, auto-validate after a short delay
    if (data.brand && data.year && data.displacement_cc && data.power_ch) {
      setTimeout(() => {
        handleManualSubmit();
      }, 1000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
      {/* Hero Section - Matching site style */}
      <section className="pt-20 py-16 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Calculateur de Prix
          </h1>
          <p className="text-lg text-gray-600">
            {subscriptionNames[abonnement] || 'Sélectionnez votre abonnement'}
          </p>
        </div>
      </section>

      <div className="container max-w-5xl mx-auto px-4 py-8">
        {/* Step 1: Vehicle Identification */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#52a675] text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Identifiez votre véhicule
              </h2>
            </div>

            {/* Carte Grise Upload Button */}
            <div className="mb-6">
              <Button
                onClick={() => setShowCarteGriseScanner(true)}
                className="w-full bg-gradient-to-r from-[#52a675] to-[#4a9566] hover:from-[#4a9566] hover:to-[#428f5c] text-white py-6 text-lg font-semibold"
                size="lg"
              >
                <Upload className="mr-3 h-6 w-6" />
                📸 Importer ma carte grise
              </Button>
              <p className="text-sm text-gray-500 text-center mt-2">
                Gagnez du temps en important votre carte grise. Nous extrairons automatiquement les informations.
              </p>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">ou saisissez manuellement</span>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-900">Saisie manuelle</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="brand">Marque *</Label>
                  <Select value={manualBrand} onValueChange={setManualBrand}>
                    <SelectTrigger id="brand" data-testid="manual-brand-select">
                      <SelectValue placeholder="Sélectionnez une marque" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {VEHICLE_BRANDS.map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="model">Modèle</Label>
                  <Input
                    id="model"
                    placeholder="Ex: Golf"
                    value={manualModel}
                    onChange={(e) => setManualModel(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="year">Année *</Label>
                  <Select value={manualYear ? String(manualYear) : ''} onValueChange={(v) => setManualYear(v)}>
                    <SelectTrigger id="year" data-testid="manual-year-select">
                      <SelectValue placeholder="Année" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {ELIGIBLE_YEARS.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="displacement">Cylindrée (cc) *</Label>
                  <Input
                    id="displacement"
                    type="number"
                    placeholder="1500"
                    value={manualDisplacement}
                    onChange={(e) => setManualDisplacement(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="power">Puissance (CH) *</Label>
                  <Input
                    id="power"
                    type="number"
                    placeholder="130"
                    value={manualPower}
                    onChange={(e) => setManualPower(e.target.value)}
                  />
                </div>
              </div>
              <Button
                onClick={handleManualSubmit}
                disabled={lookupLoading}
                className="w-full"
              >
                {lookupLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validation...
                  </>
                ) : (
                  'Valider les données'
                )}
              </Button>
            </div>

            {/* Lookup Error */}
            {lookupError && (
              <Alert variant={lookupError.startsWith('✅') ? 'default' : 'destructive'} className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{lookupError}</AlertDescription>
              </Alert>
            )}

            {/* Completing partial data */}
            {isCompletingData && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3">Compléter les informations manquantes :</h4>
                <div className="grid grid-cols-2 gap-3">
                  {missingFieldsList.includes('marque') && (
                    <div>
                      <Label htmlFor="complete-brand" className="text-sm">Marque *</Label>
                      <Select value={manualBrand} onValueChange={setManualBrand}>
                        <SelectTrigger id="complete-brand">
                          <SelectValue placeholder="Sélectionnez une marque" />
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          {VEHICLE_BRANDS.map((b) => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {missingFieldsList.includes('année') && (
                    <div>
                      <Label htmlFor="complete-year" className="text-sm">Année *</Label>
                      <Select value={manualYear ? String(manualYear) : ''} onValueChange={(v) => setManualYear(v)}>
                        <SelectTrigger id="complete-year">
                          <SelectValue placeholder="Année" />
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          {ELIGIBLE_YEARS.map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {missingFieldsList.includes('cylindrée') && (
                    <div>
                      <Label htmlFor="complete-displacement" className="text-sm">Cylindrée (cc) *</Label>
                      <Input
                        id="complete-displacement"
                        type="number"
                        value={manualDisplacement}
                        onChange={(e) => setManualDisplacement(e.target.value)}
                        placeholder="1500"
                      />
                    </div>
                  )}
                  {missingFieldsList.includes('puissance') && (
                    <div>
                      <Label htmlFor="complete-power" className="text-sm">Puissance (CH) *</Label>
                      <Input
                        id="complete-power"
                        type="number"
                        value={manualPower}
                        onChange={(e) => setManualPower(e.target.value)}
                        placeholder="130"
                      />
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleManualSubmit}
                  disabled={lookupLoading}
                  className="w-full mt-3"
                >
                  Valider les données complètes
                </Button>
              </div>
            )}

            {/* Vehicle Data Display */}
            {vehicleData && (
              <Alert className="mt-4 bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <div className="font-semibold mb-2">Véhicule identifié ({dataSource})</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Marque: {vehicleData.brand || 'N/A'}</div>
                    {vehicleData.model && <div>Modèle: {vehicleData.model}</div>}
                    <div>Année: {vehicleData.year || 'N/A'}</div>
                    <div>Cylindrée: {vehicleData.displacement_cc || 'N/A'} cc</div>
                    <div>Puissance: {vehicleData.power_ch || 'N/A'} CH</div>
                  </div>
                  {!vehicleData.complete && (
                    <div className="text-orange-600 mt-2 text-sm">
                      ⚠️ Données incomplètes. Veuillez compléter manuellement.
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Select Plan & Franchise */}
        {vehicleData && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Choisissez votre abonnement et franchise
                </h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="abonnement">Type d'abonnement</Label>
                  <Select value={abonnement} onValueChange={setAbonnement}>
                    <SelectTrigger id="abonnement">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="S">Abonnement S</SelectItem>
                      <SelectItem value="M">Abonnement M</SelectItem>
                      <SelectItem value="L">Abonnement L</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="franchise">Franchise (CHF)</Label>
                  <Select value={String(franchise)} onValueChange={(v) => setFranchise(parseInt(v))}>
                    <SelectTrigger id="franchise">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 CHF</SelectItem>
                      <SelectItem value="100">100 CHF</SelectItem>
                      <SelectItem value="200">200 CHF</SelectItem>
                      <SelectItem value="300">300 CHF</SelectItem>
                      <SelectItem value="400">400 CHF</SelectItem>
                      <SelectItem value="500">500 CHF</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Information franchise */}
              <div className="mt-4">
                <FranchiseInfoNote abonnement={abonnement} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Price Result */}
        {priceResult && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Résultat
                </h2>
              </div>

              {priceResult.success && priceResult.eligible ? (
                <div className="bg-gradient-to-br from-[#52a675] to-[#4a9566] text-white rounded-lg p-8 text-center">
                  <div className="text-lg mb-2">Votre prix mensuel</div>
                  <div className="text-5xl font-bold mb-1">
                    CHF {priceResult.price?.toFixed(2)}
                  </div>
                  <div className="text-blue-100 mb-6">par mois</div>
                  
                  <div className="bg-white/20 rounded-lg p-4 mb-6 text-sm">
                    <div className="grid grid-cols-2 gap-3 text-left">
                      <div>Abonnement: {abonnement}</div>
                      <div>Franchise: {franchise} CHF</div>
                      <div>Cylindrée: {vehicleData.displacement_cc} cc</div>
                      <div>Puissance: {vehicleData.power_ch} CH</div>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    className="bg-white text-[#52a675] hover:bg-gray-100 font-semibold"
                    onClick={() => {
                      // Navigate to subscription flow with data
                      navigate('/subscription/vehicle-info', {
                        state: {
                          vehicleData: vehicleData,
                          abonnement: abonnement,
                          franchise: franchise,
                          monthlyPrice: priceResult.price,
                          dataSource: dataSource,
                          carteGriseData: carteGriseData  // Pass OCR data for pre-filling
                        }
                      });
                    }}
                  >
                    Souscrire à cet abonnement
                  </Button>
                </div>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-semibold mb-2">Véhicule non éligible</div>
                    <ul className="list-disc list-inside space-y-1">
                      {priceResult.reasons?.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="mt-4 text-center">
                <Button variant="outline" onClick={handleReset}>
                  Nouveau calcul
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading state */}
        {calculating && !priceResult && (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-[#52a675]" />
              <p className="text-gray-600">Calcul du prix en cours...</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Carte Grise Scanner Modal */}
      <CarteGriseScanner
        open={showCarteGriseScanner}
        onClose={() => setShowCarteGriseScanner(false)}
        onConfirm={handleCarteGriseData}
      />
    </div>
  );
};

export default PriceCalculatorV4;
